import React from 'react';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatDuration } from '../utils/projectUtils';

const ProjectSummary = ({ calculatedDates, formData }) => {
  if (!calculatedDates.projet) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="text-center text-gray-500">
          <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Configurez les phases pour voir le résumé du projet</p>
        </div>
      </div>
    );
  }

  const projet = calculatedDates.projet;
  const totalBudget = (parseFloat(formData.budget_analyse_design) || 0) +
                     (parseFloat(formData.budget_build) || 0) +
                     (parseFloat(formData.budget_test) || 0);

  const getStatusColor = () => {
    if (projet.has_errors) return 'red';
    if (projet.has_warnings) return 'orange';
    return 'green';
  };

  const getStatusIcon = () => {
    if (projet.has_errors) return <AlertTriangle className="h-5 w-5" />;
    if (projet.has_warnings) return <AlertTriangle className="h-5 w-5" />;
    return <CheckCircle className="h-5 w-5" />;
  };

  const getStatusMessage = () => {
    if (projet.has_errors) return 'Configuration incomplète';
    if (projet.has_warnings) return 'Attention requise';
    return 'Projet prêt à être créé';
  };

  const statusColor = getStatusColor();
  const statusColorClasses = {
    red: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    green: 'bg-green-50 border-green-200 text-green-700'
  };

  return (
    <div className={`border rounded-lg p-4 ${statusColorClasses[statusColor]}`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Résumé du Projet
        </h4>
        <div className={`flex items-center text-sm font-medium`}>
          {getStatusIcon()}
          <span className="ml-1">{getStatusMessage()}</span>
        </div>
      </div>
      
      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="text-center bg-white rounded-lg p-3 border border-white/50">
          <div className="text-2xl font-bold text-primary-600">
            {totalBudget}
          </div>
          <div className="text-sm text-gray-600">Jours planifiés</div>
        </div>
        
        <div className="text-center bg-white rounded-lg p-3 border border-white/50">
          <div className="text-2xl font-bold text-primary-600">
            {projet.duree_totale_semaines || 0}
          </div>
          <div className="text-sm text-gray-600">Semaines estimées</div>
        </div>
        
        <div className="text-center bg-white rounded-lg p-3 border border-white/50">
          <div className="text-2xl font-bold text-primary-600">
            {projet.phases_actives}
          </div>
          <div className="text-sm text-gray-600">Phases actives</div>
        </div>
        
        <div className="text-center bg-white rounded-lg p-3 border border-white/50">
          <div className="text-2xl font-bold text-primary-600">
            {projet.capacite_moyenne ? projet.capacite_moyenne.toFixed(1) : '0'}
          </div>
          <div className="text-sm text-gray-600">Capacité moy. (j/sem)</div>
        </div>
      </div>

      {/* Dates du projet */}
      {projet.phases_actives > 0 && (
        <div className="bg-white rounded-lg p-4 border border-white/50 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900 mb-1">Période du projet</h5>
              <div className="text-sm text-gray-600">
                Du {format(projet.date_debut, 'dd MMMM yyyy', { locale: fr })} 
                {' '}au {format(projet.date_fin, 'dd MMMM yyyy', { locale: fr })}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-gray-900">
                {formatDuration(projet.duree_totale_semaines)}
              </div>
              <div className="text-sm text-gray-500">Durée totale</div>
            </div>
          </div>
        </div>
      )}

      {/* Détail des phases */}
      <div className="bg-white rounded-lg p-4 border border-white/50">
        <h5 className="font-medium text-gray-900 mb-3 flex items-center">
          <TrendingUp className="h-4 w-4 mr-2" />
          Détail des phases
        </h5>
        
        <div className="space-y-3">
          {['analyse_design', 'build', 'test'].map(phaseKey => {
            const phase = calculatedDates[phaseKey];
            const phaseName = {
              analyse_design: 'Analyse & Design',
              build: 'Développement', 
              test: 'Tests'
            }[phaseKey];
            
            const phaseColor = {
              analyse_design: 'blue',
              build: 'green',
              test: 'purple'
            }[phaseKey];

            if (!phase || phase.duree_semaines === 0) {
              return (
                <div key={phaseKey} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0 opacity-50">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 bg-${phaseColor}-300 rounded-sm mr-3`}></div>
                    <span className="text-sm text-gray-500">{phaseName}</span>
                  </div>
                  <span className="text-xs text-gray-400">Non configurée</span>
                </div>
              );
            }

            return (
              <div key={phaseKey} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center">
                  <div className={`w-3 h-3 bg-${phaseColor}-500 rounded-sm mr-3`}></div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">{phaseName}</span>
                    <div className="text-xs text-gray-500">
                      {phase.affectations_valides} personne{phase.affectations_valides !== 1 ? 's' : ''} • {phase.budget_jours}j planifiés
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {formatDuration(phase.duree_semaines)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {format(phase.date_debut, 'dd/MM', { locale: fr })} - {format(phase.date_fin, 'dd/MM', { locale: fr })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Messages d'alerte */}
      {projet.has_errors && (
        <div className="mt-4 p-3 bg-red-100 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium mb-1">Erreurs à corriger :</p>
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(calculatedDates).map(([key, phase]) => {
                  if (key === 'projet' || !phase.surcharge) return null;
                  const phaseName = {
                    analyse_design: 'Analyse & Design',
                    build: 'Développement',
                    test: 'Tests'
                  }[key];
                  return (
                    <li key={key}>Phase {phaseName} : Aucune personne affectée</li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {projet.has_warnings && !projet.has_errors && (
        <div className="mt-4 p-3 bg-orange-100 border border-orange-200 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-orange-700">
              <p className="font-medium mb-1">Attention :</p>
              <p>Vérifiez les affectations et la disponibilité des personnes.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSummary;
